import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import {
  motion,
  MotionValue,
  PanInfo,
  useAnimationControls,
  useTransform
} from 'framer-motion';
import { hapticFeedbackImpactOccurred } from '@telegram-apps/sdk-react';

import Content from './Components/Content';
import ActionButtons from './Components/ActionButtons';
import { SwipeAnimation } from './Components/SwipeAnimation.tsx';

import { CandidateUI, LikesQuota } from '@/api/matchmaker/types.ts';
import { SwipeRef, SwipeType } from '@/components/Discovery/Discovery.tsx';
import { useDiscoveryStore } from '@/store/useDiscoveryStore.ts';
import { useTooltipStore } from '@/store/useTooltipStore.ts';
import { useCloudStorage } from '@/hooks/useStorage.ts';
import {
  containerStyle,
  contentStyle
} from '@/components/Discovery/Card/styles/card-styles.ts';

const MemoizedContent = memo(Content);
const MemoizedActionButtons = memo(ActionButtons);

interface CardProps {
  matchCandidate: CandidateUI;
  handleLike: (
    candidate: CandidateUI,
    nextCardHid?: string
  ) => Promise<LikesQuota | null>;
  handlePass: (candidate: CandidateUI) => void;
  openSendGiftPopup: () => void;
  isFrontCard?: boolean;
  sharedX: MotionValue<number>;
  nextCardHid?: string;
}

// eslint-disable-next-line react/display-name
const Card = forwardRef<SwipeRef, CardProps>((props, ref) => {
  const {
    matchCandidate,
    handleLike,
    handlePass,
    openSendGiftPopup,
    isFrontCard,
    sharedX,
    nextCardHid
  } = props;
  const animControls = useAnimationControls();
  const { filterCandidates } = useDiscoveryStore();

  const backCardScale = useTransform(sharedX, [-150, 0, 150], [1, 0.9, 1]);

  const { runTooltipFlow } = useTooltipStore();

  const storageApi = useCloudStorage();

  const cardRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const swipeRight = async (
    candidate: CandidateUI,
    type?: SwipeType,
    ignoreLikeRequest = false
  ): Promise<LikesQuota | null> => {
    hapticFeedbackImpactOccurred('rigid');

    const duration = type === SwipeType.SLOW ? 0.6 : 0.4;
    cardRef.current?.classList.add('swiping');

    try {
      await animControls.start({
        x: 500,
        rotate: 20,
        transition: { duration }
      });
    } finally {
      cardRef.current?.classList.remove('swiping');
    }

    if (ignoreLikeRequest) {
      filterCandidates(candidate.profile.hid);
      return null;
    }

    if (!ignoreLikeRequest && typeof handleLike === 'function') {
      const likesQuota = await handleLike(candidate, nextCardHid);
      runTooltipFlow('like', storageApi);
      return likesQuota ?? null;
    }

    return null;
  };

  const swipeLeft = (candidate: CandidateUI, type?: string) => {
    hapticFeedbackImpactOccurred('rigid');

    const duration = type === SwipeType.SLOW ? 0.6 : 0.4;
    cardRef.current && cardRef.current.classList.add('swiping');

    void animControls
      .start({
        x: -500,
        rotate: -20,
        transition: { duration: duration },
        transitionEnd: { display: 'none' }
      })
      .then(() => {
        cardRef.current && cardRef.current.classList.remove('swiping');
        handlePass(candidate);
      });
  };

  const onClickLike = useCallback(async () => {
    if (isFrontCard) {
      const likesQuote = await swipeRight(matchCandidate, SwipeType.SLOW);

      if (likesQuote?.remaining === -1) {
        snapBack();
      }
    }
  }, [isFrontCard, matchCandidate]);

  const onClickPass = useCallback(
    () => isFrontCard && swipeLeft(matchCandidate, SwipeType.SLOW),
    [isFrontCard, matchCandidate]
  );

  useImperativeHandle(ref, () => ({
    swipeRight: ({ type, ignoreLikeRequest }) =>
      swipeRight(matchCandidate, type, ignoreLikeRequest),
    swipeLeft: (type) => swipeLeft(matchCandidate, type)
  }));

  const snapBack = () => {
    void animControls.start({
      x: 0,
      rotate: 0,
      transition: { duration: 0.3 }
    });
  };

  const onDragEnd = async (
    _: MouseEvent | TouchEvent | PointerEvent,
    info: PanInfo
  ) => {
    setIsDragging(false);

    if (Math.abs(info.offset.x) > 150) {
      hapticFeedbackImpactOccurred('rigid');

      if (info.offset.x > 0) {
        const likesQuote = await swipeRight(matchCandidate);

        if (likesQuote?.remaining === -1) {
          snapBack();
        }
      } else {
        swipeLeft(matchCandidate);
      }
    } else {
      snapBack();
    }
  };

  useEffect(() => {
    runTooltipFlow('card_change', storageApi);
  }, [matchCandidate.profile.hid]);

  return (
    <>
      <motion.div
        ref={cardRef}
        drag={isFrontCard}
        style={{
          x: isFrontCard ? sharedX : undefined,
          scale: !isFrontCard ? backCardScale : 1,
          willChange: 'transform',
          ...containerStyle
        }}
        animate={animControls}
        dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
        dragSnapToOrigin
        onDragStart={() => setIsDragging(true)}
        onDragEnd={onDragEnd}
      >
        <div className="cardContent" style={contentStyle}>
          {isFrontCard && <SwipeAnimation x={sharedX} />}

          <MemoizedContent
            matchCandidate={matchCandidate}
            isDragging={isDragging}
          />

          <MemoizedActionButtons
            handleLike={onClickLike}
            handlePass={onClickPass}
            handleReaction={openSendGiftPopup}
          />
        </div>
      </motion.div>
    </>
  );
});

export default Card;
